import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { Folder, Loader2 } from "lucide-react"
import { PostCard } from "@/components/posts/PostCard"
import { Sidebar } from "@/components/layout/Sidebar"
import { getCategoryBySlug, getPosts, getPostsWithAuthors, enrichPostsWithTags } from "@/lib/db"
import type { PostWithAuthor, Category } from "@/types"

export default function CategoryPage() {
  const { category: categorySlug } = useParams<{ category: string }>()
  const [cat, setCat] = useState<Category | null>(null)
  const [posts, setPosts] = useState<PostWithAuthor[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadCategory() {
      if (!categorySlug) return

      try {
        setLoading(true)
        const categoryData = await getCategoryBySlug(categorySlug)
        setCat(categoryData)

        const { posts: rawPosts } = await getPosts({
          category: categoryData?.id,
          status: "published",
          orderByField: "published_at",
          orderDirection: "desc",
          limitCount: 20,
        })

        const postsData = await getPostsWithAuthors(rawPosts).then(enrichPostsWithTags)
        setPosts(postsData)
      } catch (err) {
        console.error("Failed to load category:", err)
      } finally {
        setLoading(false)
      }
    }

    loadCategory()
  }, [categorySlug])

  if (loading) {
    return (
      <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <div className="flex items-center gap-2">
          <Folder className="h-8 w-8 text-indigo-600" />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{cat?.name || categorySlug}</h1>
        </div>
        {cat?.description && <p className="mt-2 text-gray-600 dark:text-gray-400">{cat.description}</p>}
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-500">
          {posts.length} {posts.length === 1 ? "post" : "posts"}
        </p>
      </div>

      <div className="flex flex-col gap-8 lg:flex-row">
        <div className="flex-1 space-y-6">
          {posts.length > 0 ? (
            posts.map(post => <PostCard key={post.id} post={post} />)
          ) : (
            <div className="py-12 text-center">
              <Folder className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No posts found</h3>
              <p className="mt-2 text-gray-500 dark:text-gray-400">No posts in this category yet.</p>
            </div>
          )}
        </div>
        <aside className="w-full lg:w-80">
          <Sidebar />
        </aside>
      </div>
    </div>
  )
}
